import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, TreeSelect, Switch, Radio, Row, Col, message } from 'antd';
import type { Rule } from 'antd/es/form';

export type FormFieldType = 'text' | 'number' | 'select' | 'tree-select' | 'password' | 'textarea' | 'switch' | 'radio' | 'json';

export interface FormFieldConfig {
  name: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  rules?: Rule[] | ((mode: 'create' | 'edit') => Rule[]);
  initialValue?: any;
  options?: { label: string; value: any; disabled?: boolean }[];
  treeData?: any[];
  fieldProps?: any;
  hidden?: boolean | ((mode: 'create' | 'edit') => boolean);
  disabled?: boolean | ((mode: 'create' | 'edit') => boolean);
  span?: number; 
}

export interface UpsertModalProps<TRow> {
  title?: React.ReactNode;
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  
  mode: 'create' | 'edit';
  record?: TRow | null; // For edit mode

  // APIs
  createApi?: (data: any) => Promise<any>;
  updateApi?: (id: string, data: any) => Promise<any>;
  
  // Form Config
  formFields: FormFieldConfig[];
  width?: number;
  
  // Hooks
  beforeSubmit?: (values: any, mode: 'create' | 'edit', record?: TRow) => any;
  beforeEdit?: (record: TRow) => any; // Transform record to form values when opening edit
  
  // Extra
  initialValues?: Record<string, any>;
}

export const UpsertModal = <TRow extends { id: any }>(props: UpsertModalProps<TRow>) => {
  const { 
    title, open, onCancel, onSuccess, mode, record,
    createApi, updateApi, formFields, width = 600,
    beforeSubmit, beforeEdit, initialValues
  } = props;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (mode === 'create') {
        const values: any = {};
        formFields.forEach(field => {
          if (field.initialValue !== undefined) {
            values[field.name] = field.initialValue;
          }
        });
        if (initialValues) {
          Object.assign(values, initialValues);
        }
        form.setFieldsValue(values);
      } else if (mode === 'edit' && record) {
        let values = { ...record };
        if (beforeEdit) {
          values = beforeEdit(record);
        }
        form.setFieldsValue(values);
      }
    }
  }, [open, mode, record, form, formFields, beforeEdit]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      let payload = values;
      if (beforeSubmit) {
        payload = beforeSubmit(values, mode, record || undefined);
      }

      setSaving(true);
      let res;
      if (mode === 'edit' && updateApi && record) {
        res = await updateApi(String(record.id), payload);
      } else if (mode === 'create' && createApi) {
        res = await createApi(payload);
      }

      if (res && typeof res === 'object' && 'code' in res && res.code !== 200 && res.code !== 201) {
        throw new Error(res.message || '操作失败');
      }

      message.success(mode === 'create' ? '创建成功' : '更新成功');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      const msg = error.response?.data?.message || error.message || '保存失败';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: FormFieldConfig) => {
    const isHidden = typeof field.hidden === 'function' ? field.hidden(mode) : field.hidden;
    if (isHidden) return null;

    const isDisabled = typeof field.disabled === 'function' ? field.disabled(mode) : field.disabled;

    let inputNode;
    switch (field.type) {
      case 'select':
        inputNode = <Select options={field.options} placeholder={`请选择${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'tree-select':
        inputNode = <TreeSelect treeData={field.treeData} placeholder={`请选择${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'password':
        inputNode = <Input.Password placeholder={`请输入${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'textarea':
        inputNode = <Input.TextArea placeholder={`请输入${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'number':
        inputNode = <InputNumber placeholder={`请输入${field.label}`} disabled={isDisabled} style={{ width: '100%' }} {...field.fieldProps} />;
        break;
      case 'switch':
        inputNode = <Switch disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'radio':
        inputNode = <Radio.Group options={field.options} disabled={isDisabled} {...field.fieldProps} />;
        break;
      case 'json':
        inputNode = <Input.TextArea placeholder={`请输入JSON格式${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
        break;
      default:
        inputNode = <Input placeholder={`请输入${field.label}`} disabled={isDisabled} {...field.fieldProps} />;
    }

    let rules: Rule[] = [];
    if (typeof field.rules === 'function') {
      rules = field.rules(mode);
    } else if (field.rules) {
      rules = field.rules;
    } else if (field.required) {
      rules = [{ required: true, message: `请输入${field.label}` }];
    }

    return (
      <Form.Item
        key={field.name}
        name={field.name}
        label={field.label}
        rules={rules}
        valuePropName={field.type === 'switch' ? 'checked' : 'value'}
      >
        {inputNode}
      </Form.Item>
    );
  };

  return (
    <Modal
      title={title || (mode === 'create' ? '新增' : '编辑')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={saving}
      width={width}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          {formFields.map((field) => {
            const fieldNode = renderField(field);
            if (!fieldNode) return null;
            return (
              <Col span={field.span || 24} key={field.name}>
                {fieldNode}
              </Col>
            );
          })}
        </Row>
      </Form>
    </Modal>
  );
};
